/**
 * Legacy publisher MOS — replicates `PublishingRTCStats` (deleted 2026-05-11)
 * for the Phase-1 ±0.3 parallel-log comparison against the new aggregator MOS,
 * and so the `connectionQualityV2Enabled` rollback knob can route the
 * indicator back to this number.
 *
 * DO NOT "fix" the bugs below — that would invalidate the parity comparison
 * and force a re-run of the validation phase.
 *
 * Preserved-for-parity bugs:
 *  1. Per-layer aggregation iterates all rids regardless of whether they share
 *     a sender, so a publisher with both camera and screenshare double-counts
 *     packets. The new aggregator dedupes by `pcKey::mid::rid`.
 *  2. `effectiveLatency = averageLatency + averageJitter * 2 + 10` — the +10
 *     packetization baseline is hardcoded rather than read from codec metadata.
 *  3. The R-factor uses a linear loss penalty `R -= lossPct * 2.5` instead of
 *     the ITU-T G.107 burst-aware form. Severe loss flattens to the same
 *     penalty as moderate loss.
 *
 * Once the V2 indicator source has been live in production for ≥2 weeks and
 * the ±0.3 criterion is met, this file should be deleted along with the
 * `legacyPublishingMOS` field on SystemStatsState (see SAMBA-8730 Phase-1).
 */

import type { Rid } from 'utils/webrtc/types';
import type { OutboundVideoFields, PublishingLayer } from 'utils/webrtc/stats/types';

const RID_VALUES: ReadonlyArray<Rid> = ['l', 'm', 'h'];

const isRid = (value: string): value is Rid =>
  (RID_VALUES as ReadonlyArray<string>).includes(value);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function legacyPublisherMOS(
  outboundVideo: ReadonlyMap<string, OutboundVideoFields>
): number | null {
  let activeLayers = 0;
  let totalRTTms = 0;
  let totalJitterMs = 0;
  let totalPacketsSent = 0;
  let totalPacketsLost = 0;

  outboundVideo.forEach((fields) => {
    if (!fields.packetsSent || fields.packetsSent <= 0) {
      return;
    }
    activeLayers += 1;
    totalRTTms += (fields.roundTripTime ?? 0) * 1000;
    totalJitterMs += fields.jitter * 1000;
    totalPacketsSent += fields.packetsSent;
    totalPacketsLost += fields.packetsLost;
  });

  if (activeLayers === 0) {
    return null;
  }

  const averageLatency = totalRTTms / activeLayers;
  const averageJitter = totalJitterMs / activeLayers;
  const effectiveLatency = averageLatency + averageJitter * 2 + 10;

  let R = 93.2;
  if (effectiveLatency < 160) {
    R -= effectiveLatency / 40;
  } else {
    R -= (effectiveLatency - 120) / 10;
  }

  const lossPct =
    totalPacketsSent > 0 ? clamp((totalPacketsLost / totalPacketsSent) * 100, 0, 100) : 0;
  R -= lossPct * 2.5;
  R = Math.max(R, 0);

  const mos = 1 + 0.035 * R + 0.000007 * R * (R - 60) * (100 - R);
  return clamp(mos, 1, 5);
}

export function extractPublishingLayers(
  outboundVideo: ReadonlyMap<string, OutboundVideoFields>
): Record<Rid, PublishingLayer> {
  const out: Partial<Record<Rid, PublishingLayer>> = {};

  outboundVideo.forEach((fields, key) => {
    const [, , ridRaw] = key.split('::');
    if (!ridRaw || !isRid(ridRaw)) {
      return;
    }

    const packetLoss =
      fields.packetsSent > 0 ? Math.round((fields.packetsLost / fields.packetsSent) * 100) : 0;

    out[ridRaw] = {
      rid: ridRaw,
      packetsSent: fields.packetsSent,
      packetsLost: fields.packetsLost,
      packetLoss,
      jitter: Math.round(fields.jitter * 1000),
      rtt: Math.round((fields.roundTripTime ?? 0) * 1000),
    };
  });

  return out as Record<Rid, PublishingLayer>;
}
